import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import CartLoyaltyLine from '@/features/loyalty/components/CartLoyaltyLine';
import CartGuestNudge from '@/features/loyalty/components/CartGuestNudge';
import {
  closeDrawer,
  removeLine,
  selectIsDrawerOpen,
  selectLinesByRestaurant,
  selectCartSubtotal,
  selectCartMode,
  setMode,
  updateLineUnitPrice,
} from './cartSlice';
import { repriceCartLines } from './repriceCartLines';
import { diagnoseCartLines, remainingPeriodsToday } from './cartAvailability';
import UnavailableItemsModal from './UnavailableItemsModal';
import UpsellModal, { type UpsellReward } from './UpsellModal';
import CartGiftsModal from './CartGiftsModal';
import type { BlockedCartLine } from './cartAvailability';
import { setMode as setCheckoutMode, setCustomerNote, setCartOption, bindRestaurant } from '@/features/checkout/checkoutSlice';
import { selectTableSession, selectIsBuffetDineIn } from '@/features/tableSession/tableSessionSlice';
import { useSharedTableCart } from '@/features/tableSession/useSharedTableCart';
import { resolveSharedCartLines, groupSharedCartLinesByGuest } from '@/features/tableSession/resolveSharedCart';
import Drawer from '@/components/ui/Drawer';
import NoteField from '@/components/ui/NoteField';
import CartLineItem from './CartLineItem';
import SharedCartLineItem from './SharedCartLineItem';
import Price from '@/components/ui/Price';
import UpsellBanner from '@/components/ui/UpsellBanner';
import { useRestaurant } from '@/features/restaurant/useRestaurant';
import { useGetMenuQuery } from '@/app/api/resourceApi';
import { selectPromotion, nextPromotionHint, eligibleDiscountSubtotal } from '@/features/checkout/selectPromotion';
import { giftTiers, groupsForMode, nextCartOptionHint, selectionsForGroups } from '@/features/checkout/cartOptions';
import { minOrderCentsForOrder } from '@/features/checkout/minOrder';
import { useGetDeliveryConfigQuery } from '@/features/checkout/deliveryApi';
import { diagnoseCartSlots, tomorrowCheckoutSlots, filterSlotsByCartAvailability, firstSlotFor } from '@/features/checkout/checkoutSlots';
import { useAllowedModes } from '@/features/checkout/useAllowedModes';
import { isChannelPaused, isOnlineOrderingPaused } from '@/features/restaurant/pausedChannels';
import { catalogName, useCatalogText } from '@/lib/localizeCatalog';
import { restaurantTz, todayIsoInTz } from '@/lib/hours';
import type { OrderMode } from '@/types';
import { useTableOrderingLock } from '@/features/tableSession/useTableOrderingLock';

export default function CartDrawer() {
  const { t } = useTranslation();
  const ct = useCatalogText();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const isOpen = useAppSelector(selectIsDrawerOpen);
  // Optional chaining on purpose: this drawer is mounted by many stores that carry
  // no `user` slice (cart-only tests); a guest is simply "not authenticated".
  const isAuthed = useAppSelector((s) => s.user?.status === 'authenticated');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lines = useAppSelector(selectLinesByRestaurant(restaurantUuid ?? ''));
  const subtotal = useAppSelector(selectCartSubtotal(restaurantUuid ?? ''));
  const mode = useAppSelector(selectCartMode(restaurantUuid ?? ''));
  const customerNote = useAppSelector((s) => s.checkout.customerNote);
  const cartOptions = useAppSelector((s) => s.checkout.cartOptions);
  const { identity, config } = useRestaurant(restaurantUuid ?? '');
  // DF-0330 — read the restaurant's own timezone, not the customer's browser.
  const tz = restaurantTz(identity ?? {});

  // SC-331: bind the checkout context to this restaurant as soon as the cart
  // mounts, so the note typed here is stored while the slice is already bound to
  // the page uuid. CheckoutPage's mount bindRestaurant(sameUuid) is then a no-op
  // and the note survives into finalization — previously the stale '' uuid made
  // that bind reset the slice and wipe the note.
  useEffect(() => {
    if (restaurantUuid) dispatch(bindRestaurant(restaurantUuid));
  }, [restaurantUuid, dispatch]);

  const tableSession = useAppSelector(selectTableSession);
  // SC-779 : c'est l'URL qui dit « sur place », pas la session. Sur un scan neuf,
  // l'ecran « Combien de personnes ? » s'affiche AVANT la jointure, donc la
  // session n'existe pas encore : le tiroir se croyait alors en vente en ligne et
  // servait au client assis le choix Emporter/Livraison, les promotions et le
  // minimum de commande a emporter — ce dernier desactivant « Passer la
  // commande ». Le panier, lui, survit dans localStorage (inter-onglets) la ou la
  // session vit en sessionStorage (par onglet) : scanner un 2e QR ouvre un nouvel
  // onglet et suffit a reunir « panier plein + session absente ». Meme source de
  // verite que le logo et le bandeau (SC-766) et que RestaurantShell.
  const onTableRoute = !!restaurantUuid && pathname.startsWith(`/${restaurantUuid}/table/`);
  const sessionOfThisRestaurant =
    tableSession !== null && tableSession.restaurantUuid === restaurantUuid;
  const isDineIn = onTableRoute || sessionOfThisRestaurant;
  // Spec 2026-08-28 paid-table-ordering-lock §4 — le tiroir et la page de
  // paiement recalculent les memes regles cote a cote et rien ne verifie
  // qu'elles s'accordent : toute garde ajoutee a l'un se cherche dans l'autre
  // (la derive a deja survecu a deux tickets, SC-753/SC-779).
  const orderingLocked = useTableOrderingLock();
  // Sur la route table tant que la jointure n'a pas eu lieu : pas de jeton
  // convive, donc aucune commande ne peut partir. On le dit ici plutot que de
  // laisser le bouton emmener le client vers le tunnel en ligne (/checkout), qui
  // sortirait de la route table et transformerait son repas sur place en une
  // commande a emporter. Une session d'un AUTRE restaurant ne compte pas : elle
  // ne fournirait pas un jeton utilisable, seulement un jeton de table etranger
  // dans l'URL de paiement.
  const awaitingTableJoin = onTableRoute && !sessionOfThisRestaurant;
  const { data: menu, refetch: refetchMenu } = useGetMenuQuery(
    restaurantUuid ?? '',
    { skip: !restaurantUuid },
  );
  // SC-169: shared-cart mode renders the server-side table cart from the poll,
  // grouped per guest (客人 1 / 客人 2 …, current guest marked "你").
  const shared = useSharedTableCart();
  const isShared = shared.isShared;
  const sharedLines = resolveSharedCartLines(shared.lines, menu);
  const sharedGroups = groupSharedCartLinesByGuest(sharedLines, shared.guestUuid);
  // SC-427: show per-line prices + a total for the shared dine-in cart before
  // sending. Buffet is à-volonté (price-less), so prices are shown only in
  // ordering mode.
  const isBuffet = useAppSelector(selectIsBuffetDineIn);
  const showSharedPrices = !isBuffet;
  const sharedSubtotal = sharedLines.reduce((sum, l) => sum + l.unitPrice * l.qty, 0);
  // SC-570: shared with CheckoutPage — "delivery" is offered only when sale_api
  // can actually quote it, not merely when `enable_livraison` is on. Offering it
  // here on the looser flag is what made the checkout swap it for "A emporter".
  // Dine-in has no takeaway/delivery choice — it's eaten at the table.
  const { modes: onlineModes, resolved: modesResolved } = useAllowedModes(restaurantUuid ?? '');
  const allowedModes: OrderMode[] = isDineIn ? [] : onlineModes;

  // SC-570: a `delivery` pick persists per restaurant, so it outlives the
  // restaurant losing its delivery zone. Fall back to the first allowed mode and
  // say so — the customer must not carry a phantom delivery order to checkout.
  // Only once both backends have answered: while the list is still the
  // provisional "known-orderable" one, a stored `delivery` pick is missing from
  // it merely because we don't know yet, and resetting on that would drop a
  // perfectly valid choice — with a banner blaming the restaurant for it.
  const [deliveryDropped, setDeliveryDropped] = useState(false);
  useEffect(() => {
    if (!modesResolved) return;
    if (allowedModes.length === 0 || allowedModes.includes(mode)) return;
    if (mode === 'delivery') setDeliveryDropped(true);
    dispatch(setMode({ restaurantUuid: restaurantUuid ?? '', mode: allowedModes[0] }));
  }, [modesResolved, allowedModes, mode, restaurantUuid, dispatch]);

  // SC-570: a livraison-only restaurant whose delivery zone is unconfigured has
  // no mode left at all. There is nothing to fall back to, so block here with
  // the reason instead of letting the customer reach a checkout that can only
  // greet them with a dead "Passer la commande".
  const noModeAvailable = !isDineIn && modesResolved && allowedModes.length === 0;

  // SC-587: "Site ouvert" OFF (or the online-ordering feature disabled on the
  // account) is enforced on the menu — the add buttons disappear — but never
  // reached the cart, so a basket filled a minute earlier still offered a live
  // "Passer la commande" that sale_api answers with 409 online_ordering_disabled.
  // A closed day / vacation already surfaces through the slot diagnosis below;
  // this covers the two master switches, which leave the hours untouched.
  // Only an explicit `false` pauses the cart: hubApi defaults both flags to true
  // when the payload omits them, and a missing flag must not take a live
  // restaurant offline at the last step.
  // SC-1434: the pause is per channel. Takeaway and delivery both paused is the
  // closed site; a single one paused only matters here when it was the last
  // mode the restaurant offers.
  const pausedMode = identity
    ? isChannelPaused(identity, 'take_out') || isChannelPaused(identity, 'delivery')
    : false;
  const orderingPaused = !isDineIn && (
    (identity ? isOnlineOrderingPaused(identity) : false)
    || identity?.onlineOrderingFeatureEnabled === false
    || (noModeAvailable && pausedMode)
  );

  // SC-524: this drawer keeps the menu query subscribed for the whole session,
  // so its cache outlives any back-office price change while every line holds
  // the price frozen at add-time. Refetch each time the drawer opens and
  // reconcile the lines, otherwise the customer reads a subtotal (and a
  // promotion threshold) the order will not honour — CheckoutPage already
  // re-prices on mount, which only moved the surprise one screen later.
  const [priceChangedNames, setPriceChangedNames] = useState<string[]>([]);
  // Keep the previous array when the names are unchanged: this effect is keyed
  // on the menu object, so a fresh state array on every fire would re-render,
  // hand back a new menu reference and spin forever.
  const setNamesIfChanged = (next: string[]) =>
    setPriceChangedNames((prev) =>
      prev.length === next.length && prev.every((name, i) => name === next[i]) ? prev : next,
    );
  useEffect(() => {
    if (!isOpen) {
      setNamesIfChanged([]);
      return;
    }
    if (restaurantUuid) refetchMenu();
    // setNamesIfChanged is a plain closure over setState — stable in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, restaurantUuid, refetchMenu]);
  useEffect(() => {
    if (!isOpen) return;
    const changed = repriceCartLines(lines, menu, isBuffet, isDineIn ? 'dine_in' : mode);
    for (const line of changed) {
      dispatch(updateLineUnitPrice({
        restaurantUuid: restaurantUuid ?? '',
        lineId: line.lineId,
        unitPrice: line.unitPrice,
      }));
    }
    setNamesIfChanged(changed.map((line) => line.name));
    // Re-running on `lines` would loop after every dispatch — gate on the menu
    // snapshot instead and read lines fresh on each fire.
    // SC-1092 : `mode` en fait partie. Un plat peut couter plus cher en
    // livraison qu'a emporter ; basculer le mode ici sans recalculer laissait le
    // tiroir afficher l'ancien total, et le nouveau n'apparaissait qu'au
    // paiement — c'est ce decalage qui finissait en litige.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, isOpen, restaurantUuid, dispatch, isBuffet, isDineIn, mode]);

  // Promotion preview — mirrors the checkout (selectPromotion operates in cents).
  // Keeps the drawer total in sync with the discount.
  const orderMode = mode === 'delivery' ? 'livraison' : 'emporter';
  const promotions = config?.promotions ?? [];
  // DF-0212 — cf. CheckoutPage : le pourcentage ne porte que sur les lignes
  // dont le produit ET la catégorie acceptent la remise.
  const eligibleSubtotal = eligibleDiscountSubtotal(lines, menu);
  // SC-1081 — validite des promotions au jour civil du restaurant.
  const promoToday = todayIsoInTz(tz);
  const promoMatch = selectPromotion(promotions, subtotal, orderMode, eligibleSubtotal, promoToday);
  const promoHint = promoMatch ? null : nextPromotionHint(promotions, subtotal, orderMode, promoToday);
  // SC-779 : « aucune reduction reservee a Emporter ne doit etre appliquee ».
  // L'indice etait deja gate plus bas, la remise elle-meme ne l'etait pas : le
  // tiroir affichait un total remise que le paiement (`discount = isDineIn ? 0`,
  // cf. CheckoutPage) reprend au prix plein, un ecran plus loin et sans un mot.
  const discountCents = isDineIn ? 0 : (promoMatch?.discountAmountCents ?? 0);

  // SC-748 — ce qu'il manque pour débloquer la question la plus proche
  // (« Plus que 5,00 € pour débloquer Un coca offert »). C'est ici qu'elle sert
  // le plus : le client garnit encore son panier, alors qu'à la validation il a
  // déjà décidé.
  // Le mode de vente d'une commande a table est `dine_in`, jamais `mode` (qui
  // ne connait qu'emporter/livraison) — meme correction que SC-753 cote page de
  // paiement, jamais reportee ici : le tiroir posait au serveur la question
  // d'un autre mode de vente, et le relanceur d'avant-validation l'arretait
  // pour rien au moment d'envoyer en cuisine.
  const cartOptionGroupsForMode = groupsForMode(menu?.cartOptionGroups, isDineIn ? 'dine_in' : mode);
  const cartOptionHint = nextCartOptionHint(cartOptionGroupsForMode, subtotal);

  // SC-1438 — les cadeaux (questions a palier) se voient et se choisissent des
  // le panier. Le choix est la reponse de l'etape « Avant de valider » : meme
  // etat, la page de paiement la retrouve cochee.
  const gifts = giftTiers(cartOptionGroupsForMode, subtotal);
  const hasGifts = gifts.unlocked.length > 0 || gifts.locked.length > 0;
  const giftSelections = selectionsForGroups(gifts.unlocked, cartOptions);
  const chosenGiftNames = gifts.unlocked.flatMap((group) => {
    const chosen = giftSelections.find((s) => s.groupId === group.id)?.options ?? [];
    return group.options.filter((o) => chosen.some((c) => c.id === o.id)).map((o) => catalogName(o));
  });
  const giftAwaitingChoice = gifts.unlocked.length > 0 && chosenGiftNames.length === 0;
  const [giftsOpen, setGiftsOpen] = useState(false);

  // SC-554 — minimum de commande par mode de vente. Compare au sous-total
  // ARTICLES, comme sale_api : les frais de livraison (calcules au paiement)
  // ne doivent pas aider a atteindre le seuil. `dine_in` n'est jamais concerne.
  // Filet purement UX — l'autorite reste le 409 order_below_minimum du serveur.
  // SC-1155 : sans adresse, pas de devis -> le plus petit minimum des zones.
  const { currentData: deliveryConfig } = useGetDeliveryConfigQuery(restaurantUuid ?? '', {
    skip: !restaurantUuid,
  });
  const minOrderCents = minOrderCentsForOrder(config, isDineIn ? 'dine_in' : orderMode, deliveryConfig);
  const belowMinimum = minOrderCents > 0 && subtotal < minOrderCents;

  // Delivery fee is distance-based and computed by sale_api at checkout
  // (compute_quote → tier match); the drawer doesn't have the address yet,
  // so it shows the item total only and announces the fee at checkout.

  // Pre-checkout slot check: if no pickup slot can exist for this cart today
  // (restaurant done for the day, or a midi/soir-only product filters every
  // remaining slot out), say so HERE and block "Passer la commande" — the
  // checkout page could only tell the customer after the fact.
  const prepMin = mode === 'delivery'
    ? (config?.prepTimeLivraisonMin ?? 0)
    : (config?.prepTimeEmporterMin ?? 0);
  // SC-1465 — premier créneau du mode (auto ou fixé par jour).
  const firstSlot = firstSlotFor(identity, mode);
  const slotDiagnosis = useMemo(() => {
    if (isDineIn || !isOpen || lines.length === 0 || !identity?.hours || !config) return null;
    return diagnoseCartSlots(
      identity.hours, new Date(), prepMin, identity.closures ?? [], lines, menu?.items ?? [], tz, firstSlot,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDineIn, isOpen, lines, identity, config, prepMin, firstSlot, menu, tz]);
  const slotBlocked = slotDiagnosis !== null && slotDiagnosis.kind !== 'ok';
  // J+1: check if tomorrow has slots when today is closed
  const hasTomorrowSlots = useMemo(() => {
    if (!slotBlocked || slotDiagnosis?.kind !== 'closed') return false;
    if (!identity?.hours) return false;
    const tmrSlots = tomorrowCheckoutSlots(identity.hours, new Date(), undefined, identity.closures, tz, prepMin, firstSlot);
    return filterSlotsByCartAvailability(tmrSlots, lines, menu?.items ?? []).length > 0;
  }, [slotBlocked, slotDiagnosis?.kind, identity, lines, menu, tz, prepMin, firstSlot]);
  // Le bandeau qui dit « mais vous pouvez commander pour demain » ne doit pas
  // griser « Passer la commande » : la page de paiement sait programmer un
  // creneau J+1 (toggle Aujourd'hui/Demain), le tiroir la coupait avant.
  const slotBlocksCheckout = slotBlocked && !hasTomorrowSlots;
  const conflictNames = slotDiagnosis?.kind === 'conflict'
    ? slotDiagnosis.blockingIds
        .map((id) => {
          const item = menu?.items.find((it) => it.id === id);
          const name = (() => {
            const snap = lines.find((l) => l.itemId === id)?.snapshot;
            return snap ? catalogName({ ...snap, translations: item?.translations ?? snap.translations }) : '';
          })();
          const tag = item && !item.active_dinner ? t('cart.lunchOnlyTag') : t('cart.dinnerOnlyTag');
          return `${name} (${tag})`;
        })
        .join(', ')
    : '';

  // SC-587: everything the back-office can do to a cart line after it was added
  // — deactivate the product or its category, switch it off for the selected
  // mode, restrict it to the other service, drop one of its options. Until now
  // every one of these was tolerated client-side (repriceCartLines and the slot
  // filter both skip a line whose product left the menu), so the cart looked
  // healthy and the order died on an opaque 400 at payment. The lines are marked
  // in red below and block "Passer la commande" until they are removed.
  const blockedLines = useMemo<BlockedCartLine[]>(() => {
    if (!isOpen || lines.length === 0 || !menu) return [];
    // Dine-in is served now, not scheduled, and sale_api's period guard only
    // runs on the online branch — leave the period dimension out of it there.
    const remainingPeriods = !isDineIn && identity?.hours
      ? remainingPeriodsToday(identity.hours, new Date(), prepMin, identity.closures ?? [], tz, firstSlot)
      : new Set<'lunch' | 'dinner'>();
    return diagnoseCartLines({
      lines, menu, mode: isDineIn ? 'dine_in' : mode, remainingPeriods,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lines, menu, identity, prepMin, firstSlot, isDineIn, mode, tz, ct.lang]);
  const blockedByLineId = useMemo(
    () => new Map(blockedLines.map((b) => [b.lineId, b.reason])),
    [blockedLines],
  );
  const dropBlockedLines = (entries: BlockedCartLine[]) => {
    for (const entry of entries) {
      dispatch(removeLine({ restaurantUuid: restaurantUuid ?? '', lineId: entry.lineId }));
    }
  };

  // The red marks above only refresh when the drawer opens. A customer who never
  // reloads can sit on the page while the admin deactivates a dish, so the last
  // gate before leaving the cart is a fresh menu + a re-check, surfaced as a
  // one-click removal instead of a dead end.
  const [pendingBlocked, setPendingBlocked] = useState<BlockedCartLine[]>([]);
  const [checkingOut, setCheckingOut] = useState(false);

  // La relance au clic sur « Passer la commande ». Le bandeau rouge du pied de
  // tiroir se lit passivement ; une modale, elle, demande une réponse — c'est
  // elle qui transforme « il me manque 5 € » en un plat de plus.
  const [upsellSeen, setUpsellSeen] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  // Un panier qui bouge, c'est un nouveau seuil a annoncer : on rearme. Sans ce
  // rearmement, le client qui ajoute un plat puis revalide ne saurait pas qu'il
  // ne lui manque plus que 5 € la ou il en manquait 20.
  useEffect(() => {
    setUpsellSeen(false);
  }, [subtotal]);

  // Ce que le panier laisse sur la table, dit en clair. Recalcule a chaque
  // rendu comme les indices dont il derive : la modale est fermee la plupart du
  // temps, memoiser une liste de deux entrees ne paierait pas son bruit.
  const upsellRewards: UpsellReward[] = [];
  // Le serveur exclut les promotions du sur place (`discount = 0` au paiement,
  // cf. CheckoutPage) : ne pas promettre a table une remise qui ne viendra pas.
  if (!isDineIn && promoHint) {
    upsellRewards.push({
      id: `promo-${promoHint.rule.id}`,
      reward: t('cart.upsell.rewardPromo', { percent: promoHint.rule.discountPercent }),
      gapCents: promoHint.gapCents,
    });
  }
  if (cartOptionHint) {
    upsellRewards.push({
      id: `option-${cartOptionHint.group.id}`,
      reward: t('cart.upsell.rewardOption', { label: catalogName(cartOptionHint.group) }),
      gapCents: cartOptionHint.gapCents,
    });
  }

  const goToCheckout = () => {
    dispatch(setCheckoutMode(mode));
    dispatch(closeDrawer());
    // Keep dine-in checkout under the table token so the URL stays distinct from
    // the online-ordering checkout (and dine-in mode survives a refresh there).
    if (isDineIn && tableSession) {
      navigate(`/${restaurantUuid}/table/${tableSession.tableToken}/checkout`);
    } else {
      navigate(`/${restaurantUuid}/checkout`);
    }
  };

  const onCheckout = async () => {
    if (isShared) {
      goToCheckout();
      return;
    }
    setCheckingOut(true);
    // A refetch that fails (offline, resource_api down) must not strand the
    // customer in the cart: fall through on the snapshot we already have — the
    // server-side guards still stand behind us.
    let fresh = menu;
    try {
      fresh = (await refetchMenu().unwrap()) ?? menu;
    } catch {
      fresh = menu;
    }
    setCheckingOut(false);
    const remainingPeriods = !isDineIn && identity?.hours
      ? remainingPeriodsToday(identity.hours, new Date(), prepMin, identity.closures ?? [], tz, firstSlot)
      : new Set<'lunch' | 'dinner'>();
    const blocked = diagnoseCartLines({
      lines, menu: fresh, mode: isDineIn ? 'dine_in' : mode, remainingPeriods,
    });
    if (blocked.length > 0) {
      setPendingBlocked(blocked);
      return;
    }
    // Derniere relance avant de quitter le menu : ici completer coute un clic,
    // a la page de paiement cela coute le formulaire deja commence.
    if (!upsellSeen && upsellRewards.length > 0) {
      setUpsellOpen(true);
      return;
    }
    goToCheckout();
  };

  const onRemoveBlockedAndContinue = () => {
    const removed = pendingBlocked;
    dropBlockedLines(removed);
    setPendingBlocked([]);
    if (removed.length < lines.length) goToCheckout();
  };

  // Une relance a laquelle le client a repondu ne revient pas tant que le
  // panier n'a pas bouge — quelle que soit la reponse, croix comprise.
  const dismissUpsell = () => {
    setUpsellSeen(true);
    setUpsellOpen(false);
  };
  const onUpsellAddMore = () => {
    dismissUpsell();
    dispatch(closeDrawer());
  };
  const onUpsellContinue = () => {
    dismissUpsell();
    goToCheckout();
  };

  return (
    <>
    <Drawer open={isOpen} onClose={() => dispatch(closeDrawer())} ariaLabel={t('cart.title')}>
      <div className="px-4 py-0.5 border-b-2 border-ink flex items-center justify-between">
        <h2 className="font-display text-xl">{t('cart.title')}</h2>
        <button
          type="button"
          onClick={() => dispatch(closeDrawer())}
          className="bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn font-display text-xl leading-none w-8 h-8 flex items-center justify-center"
          aria-label={t('cart.close')}
        >×</button>
      </div>
      {isShared ? (
        <>
          <div className="px-4 py-1.5 border-b-[1px] border-ink/30 font-mono text-xs text-ink/70">
            {t('tableShared.banner')}
          </div>
          <div className="flex-1 overflow-y-auto px-4">
            {sharedLines.length === 0 ? (
              <div className="py-12 text-center font-mono text-ink/60">
                <p className="mb-4">{t('tableShared.empty')}</p>
                <button
                  type="button"
                  onClick={() => dispatch(closeDrawer())}
                  className="bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-4 py-2 font-display"
                >
                  {t('cart.continueShopping')}
                </button>
              </div>
            ) : (
              sharedGroups.map((group) => (
                <div key={group.guestUuid || group.index} className="pt-2">
                  <p className="font-display text-sm text-mint-deep m-0 mb-0.5">
                    {group.isMine
                      ? t('tableShared.guestLabelYou', { n: group.index })
                      : t('tableShared.guestLabel', { n: group.index })}
                  </p>
                  {group.lines.map((line) => (
                    <SharedCartLineItem
                      key={line.lineId}
                      line={line}
                      guestToken={shared.guestToken}
                      showPrice={showSharedPrices}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
          {sharedLines.length > 0 && (
            <div className="border-t-2 border-ink p-4 bg-cream space-y-2">
              {showSharedPrices ? (
                <div className="flex items-center justify-between font-display text-lg">
                  <span>{t('order.total')}</span>
                  <Price amountCents={sharedSubtotal} />
                </div>
              ) : (
                <p className="font-mono text-xs text-ink/60">{t('tableShared.priceNote')}</p>
              )}
              <button
                type="button"
                onClick={onCheckout}
                className="w-full bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn py-3 font-display shadow-stamp-sm"
              >
                {t('checkout.placeOrder')}
              </button>
            </div>
          )}
        </>
      ) : (
      <>
      {allowedModes.length > 0 && (
        <div className="px-4 py-1.5 border-b-[1px] border-ink/30 flex gap-2">
          {allowedModes.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => dispatch(setMode({ restaurantUuid: restaurantUuid ?? '', mode: m }))}
              className={`flex-1 py-2 border-2 border-ink rounded-btn font-display text-sm ${
                mode === m ? 'bg-mint text-[var(--on-primary)]' : 'bg-paper'
              }`}
            >
              {t(m === 'delivery' ? 'order.modeDelivery' : 'order.modeTakeaway')}
            </button>
          ))}
        </div>
      )}
      {deliveryDropped && (
        <div
          role="status"
          className="mx-4 mt-2 border-2 border-ink bg-cream p-3 font-mono text-xs shadow-stamp"
          data-testid="cart-delivery-unavailable-banner"
        >
          {t('cart.deliveryUnavailable')}
        </div>
      )}
      {lines.length > 0 && priceChangedNames.length > 0 && (
        <div
          role="status"
          className="mx-4 mt-2 border-2 border-ink bg-cream p-3 font-mono text-xs shadow-stamp"
          data-testid="cart-price-change-banner"
        >
          {t('cart.priceUpdated', { names: priceChangedNames.join(', ') })}
        </div>
      )}
      {/* SC-1438 — `min-h-24` : sur un petit telephone le pied prenait toute la
          hauteur et les plats du panier disparaissaient ; c'est lui qui defile. */}
      <div className="flex-1 min-h-24 overflow-y-auto px-4">
        {lines.length === 0 ? (
          <div className="py-12 text-center font-mono text-ink/60">
            <p className="mb-4">{t('cart.empty')}</p>
            <button
              type="button"
              onClick={() => dispatch(closeDrawer())}
              className="bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn px-4 py-2 font-display"
            >
              {t('cart.continueShopping')}
            </button>
          </div>
        ) : (
          lines.map((line) => (
            <CartLineItem
              key={line.lineId}
              line={line}
              restaurantUuid={restaurantUuid ?? ''}
              optionGroups={menu?.optionGroups ?? []}
              restaurantLogoUrl={identity?.logoUrl ?? null}
              restaurantName={identity?.name ?? ''}
              themeColor={identity?.theme.primary ?? '#1f2937'}
              blockReason={blockedByLineId.get(line.lineId)}
            />
          ))
        )}
      </div>
      {lines.length > 0 && (
        <div className="border-t-2 border-ink px-4 pt-1.5 pb-1 bg-paper">
          <label htmlFor="cart-customer-note" className="block font-mono text-sm text-ink/70 mb-0.5">
            {t('cart.noteLabel')}
          </label>
          <NoteField
            id="cart-customer-note"
            className="w-full border-2 border-ink p-2 font-mono text-sm bg-paper resize-none"
            counterClassName="text-end font-mono text-xs text-ink/60"
            rows={2}
            maxLength={280}
            placeholder={
              mode === 'delivery'
                ? t('cart.notePlaceholderDelivery')
                : t('cart.notePlaceholderTakeaway')
            }
            value={customerNote}
            onCommit={(next) => dispatch(setCustomerNote(next))}
          />
        </div>
      )}
      {lines.length > 0 && (
        // SC-1438 — `min-h-0 overflow-y-auto` + bouton `sticky` : sur un petit
        // telephone les bandeaux empiles depassaient l'ecran et « Passer la
        // commande » sortait du tiroir, sans rien a faire defiler.
        <div className="border-t-2 border-ink p-4 bg-cream space-y-2 min-h-0 overflow-y-auto">
          {discountCents > 0 ? (
            <>
              <div className="flex justify-between font-mono text-sm text-ink/70">
                <span>{t('order.subtotal')}</span><Price amountCents={subtotal} />
              </div>
              <div className="flex justify-between font-mono text-sm text-hot-pink">
                <span>🎁 {promoMatch?.rule.label || t('cart.promotionFallback')}</span>
                <Price amountCents={-discountCents} />
              </div>
              <div className="flex justify-between font-display text-lg">
                <span>{t('order.total')}</span><Price amountCents={subtotal - discountCents} />
              </div>
            </>
          ) : (
            <div className="flex justify-between font-display text-lg">
              <span>{t('order.total')}</span><Price amountCents={subtotal} />
            </div>
          )}
          {/* Le serveur (sale_api) exclut les promotions du sur place — le pied
              de tiroir promettait donc au serveur, table 2 sous les yeux, une
              remise qui n'arriverait jamais. Meme garde que le total plus haut
              (`discount` est deja nul en sur place) et que la relance. */}
          {!isDineIn && promoHint && (
            <UpsellBanner className="w-full" testId="cart-promo-hint">
              {t('cart.promoHint', {
                amount: (promoHint.gapCents / 100).toFixed(2).replace('.', ','),
                percent: promoHint.rule.discountPercent,
              })}
            </UpsellBanner>
          )}
          {cartOptionHint && (
            <UpsellBanner className="w-full" testId="cart-option-hint">
              {t('cart.cartOptionHint', {
                amount: (cartOptionHint.gapCents / 100).toFixed(2).replace('.', ','),
                label: catalogName(cartOptionHint.group),
              })}
            </UpsellBanner>
          )}
          {hasGifts && (
            <button
              type="button"
              onClick={() => setGiftsOpen(true)}
              data-testid="cart-gifts-button"
              // Encre pleine, pas le rouge des bandeaux : pose sous eux, un
              // troisieme bloc rouge se lisait comme un bandeau de plus.
              className={`w-full flex items-center gap-2 border-2 border-solid border-ink rounded-btn px-3 py-2 font-display text-base text-start shadow-stamp-sm ${
                giftAwaitingChoice ? 'bg-ink text-paper cart-gifts-pulse' : 'bg-paper text-ink'
              }`}
            >
              <span aria-hidden="true" className="flex-none text-xl leading-none">🎁</span>
              <span className="min-w-0 flex-1 truncate">
                {chosenGiftNames.length > 0
                  ? t('cart.gifts.chosen', { names: chosenGiftNames.join(', ') })
                  : gifts.unlocked.length > 0
                    ? t('cart.gifts.choose')
                    : t('cart.gifts.view')}
              </span>
              <span className="flex-none font-mono text-xs">
                {chosenGiftNames.length > 0 && <span className="underline">{t('cart.gifts.change')}</span>} ›
              </span>
              <style>{`
                @keyframes cart-gifts-pulse {
                  0%, 100% { transform: scale(1); }
                  50% { transform: scale(1.025); }
                }
                .cart-gifts-pulse { animation: cart-gifts-pulse 1.4s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) {
                  .cart-gifts-pulse { animation: none; }
                }
              `}</style>
            </button>
          )}
          {mode === 'delivery' && (
            <div className="font-mono text-xs text-ink/60">
              {t('cart.deliveryFeeAtCheckout')}
            </div>
          )}
          {blockedLines.length > 0 && (
            <div
              role="alert"
              className="border-2 border-sale-red bg-cream p-3 font-mono text-xs shadow-stamp space-y-2"
              data-testid="cart-blocked-lines-banner"
            >
              <p className="m-0">
                {/* `n`, not `count`: a `count` option sends i18next looking for
                    plural-suffixed keys the 5 locales don't define. */}
                {t('cart.blocked.banner', { n: blockedLines.length })}
              </p>
              <button
                type="button"
                onClick={() => dropBlockedLines(blockedLines)}
                className="w-full bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn py-2 font-display"
                data-testid="cart-blocked-remove-all"
              >
                {t('cart.blocked.removeAll')}
              </button>
            </div>
          )}
          {slotBlocked && (
            <div
              role="alert"
              className="border-2 border-sale-red bg-cream p-3 font-mono text-xs shadow-stamp"
              data-testid="cart-no-slot-banner"
            >
              {slotDiagnosis?.kind === 'closed'
                ? (hasTomorrowSlots ? t('cart.noSlotsTodayTryTomorrow') : t('cart.noSlotsToday'))
                : t('cart.slotConflict', { products: conflictNames })}
            </div>
          )}
          {belowMinimum && (
            <div
              role="alert"
              className="border-2 border-sale-red bg-cream p-3 font-mono text-xs shadow-stamp"
              data-testid="cart-below-minimum-banner"
            >
              {t('cart.belowMinimum', {
                minimum: (minOrderCents / 100).toFixed(2).replace('.', ','),
                missing: ((minOrderCents - subtotal) / 100).toFixed(2).replace('.', ','),
              })}
            </div>
          )}
          {orderingPaused && (
            <div
              role="alert"
              className="border-2 border-sale-red bg-cream p-3 font-mono text-xs shadow-stamp"
              data-testid="cart-ordering-paused-banner"
            >
              {t('cart.orderingPaused')}
            </div>
          )}
          {noModeAvailable && !orderingPaused && (
            <div
              role="alert"
              className="border-2 border-sale-red bg-cream p-3 font-mono text-xs shadow-stamp"
              data-testid="cart-no-mode-banner"
            >
              {t('cart.deliveryUnavailable')}
            </div>
          )}
          {awaitingTableJoin && (
            <div
              role="alert"
              className="border-2 border-sale-red bg-cream p-3 font-mono text-xs shadow-stamp"
              data-testid="cart-awaiting-table-join-banner"
            >
              {t('cart.awaitingTableJoin')}
            </div>
          )}
          {orderingLocked && (
            <div
              role="alert"
              className="border-2 border-sale-red bg-cream p-3 font-mono text-xs shadow-stamp"
              data-testid="cart-ordering-locked-banner"
            >
              {t('tablePayment.lockedCartBlocked')}
            </div>
          )}
          {/* SC-1091 M8 — a usable loyalty coupon, one tap from being applied. */}
          {isAuthed && !isDineIn && (
            <CartLoyaltyLine restaurantUuid={restaurantUuid ?? ''} subtotalCents={subtotal} />
          )}
          {/* SC-1091 — a GUEST is told what logging in would earn (silent when the
              program is off, on a table order, or when the call fails). */}
          {!isAuthed && !isDineIn && (
            <CartGuestNudge restaurantUuid={restaurantUuid ?? ''} subtotalCents={subtotal} />
          )}
          <button
            type="button"
            onClick={onCheckout}
            disabled={
              slotBlocksCheckout || belowMinimum || noModeAvailable || orderingPaused
              || awaitingTableJoin || orderingLocked || blockedLines.length > 0 || checkingOut
            }
            className="sticky bottom-0 w-full bg-mint text-[var(--on-primary)] border-2 border-ink rounded-btn py-3 font-display shadow-stamp-sm disabled:opacity-50"
          >
            {checkingOut ? '...' : t('checkout.placeOrder')}
          </button>
        </div>
      )}
      </>
      )}
    </Drawer>
    {/* Outside the Drawer on purpose: the drawer's <aside> is permanently
        transformed (slide-in), which makes it the containing block for any
        `fixed` descendant — the modal would be boxed inside 420px. */}
    <UnavailableItemsModal
      open={pendingBlocked.length > 0}
      blocked={pendingBlocked}
      onRemoveAll={onRemoveBlockedAndContinue}
      onClose={() => setPendingBlocked([])}
      canContinueAfterRemoval={pendingBlocked.length < lines.length}
    />
    <UpsellModal
      open={upsellOpen}
      rewards={upsellRewards}
      onAddMore={onUpsellAddMore}
      onContinue={onUpsellContinue}
      onClose={dismissUpsell}
    />
    <CartGiftsModal
      open={giftsOpen}
      unlocked={gifts.unlocked}
      locked={gifts.locked}
      selections={giftSelections}
      onChange={(selection) => dispatch(setCartOption(selection))}
      onAddMore={() => {
        setGiftsOpen(false);
        dispatch(closeDrawer());
      }}
      onClose={() => setGiftsOpen(false)}
    />
    </>
  );
}
